import React, { useState, useEffect, useRef } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import {
  Home, Grid3X3, Crown, ShoppingBag, Lock,
  LogOut, Code, Mail, FileText, Share2, ChevronLeft, ChevronRight,
  Shield, Files, Sparkles, Palette, Globe2, BrainCircuit,
  Wrench, BookOpen, Zap
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { useAuth } from '@/hooks/useAuth';
import { useProfile } from '@/hooks/useProfile';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import PremiumBadge from '@/components/PremiumBadge';
import NewBadge from '@/components/NewBadge';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { ScrollArea } from '@/components/ui/scroll-area';
import tamLogo from '@/assets/tam-logo.png';
import { tools, type ToolCategory } from '@/lib/tools-config';

interface SidebarProps {
  isCollapsed: boolean;
  onToggle: () => void;
  isMobileOpen?: boolean;
  onMobileClose?: () => void;
}

const mainNavItems = [
  { icon: Home, label: 'Dashboard', path: '/dashboard', requiresPremium: false },
  { icon: Grid3X3, label: 'All Tools', path: '/dashboard/tools', requiresPremium: false, tourId: 'all-tools' },
  { icon: Crown, label: 'Pro Tools', path: '/dashboard/pro-tools', requiresPremium: false, isPro: true },
  { icon: Globe2, label: 'My Sites', path: '/dashboard/my-sites', requiresPremium: false, tourId: 'my-sites' },
  { icon: ShoppingBag, label: 'Catalogue', path: '/dashboard/catalogue', requiresPremium: false, badge: 'HOT', badgeColor: 'bg-red-500 text-white' },
  { icon: Lock, label: 'The Vault', path: '/dashboard/vault', requiresPremium: false },
  { icon: BookOpen, label: 'Guide', path: '/dashboard/guide', requiresPremium: false, tourId: 'guide' },
];

// Category icon map
const categoryIcons: Record<ToolCategory, React.ElementType> = {
  ai: Sparkles,
  developer: Code,
  email: Mail,
  document: FileText,
  pdf: Files,
  social: Share2,
  design: Palette,
  utilities: Wrench,
  pro: Crown,
};

const categoryLabels: Record<ToolCategory, string> = {
  ai: 'AI Tools',
  developer: 'Developer',
  email: 'Email',
  document: 'Document',
  pdf: 'PDF Tools',
  social: 'Social',
  design: 'Design',
  utilities: 'Utilities',
  pro: 'Pro Tools',
};

// Build sidebar tool lists from centralized tools-config
const sidebarCategories: ToolCategory[] = ['ai', 'developer', 'email', 'document', 'pdf', 'social', 'design', 'utilities'];

function getToolsForCategory(category: ToolCategory) {
  return tools
    .filter(t => t.category === category && !t.isPro)
    .map(t => ({
      label: t.name,
      path: t.path,
      icon: t.icon,
      isNew: t.isNew,
    }));
}

const liveSportsTool = { label: 'Live Sports', path: '/dashboard/tools/live-sports', icon: Zap, isNew: true, isPro: true };

const Sidebar = ({ isCollapsed, onToggle, isMobileOpen, onMobileClose }: SidebarProps) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, signOut } = useAuth();
  const { isPremium, isAdmin, premiumTier, username, profile } = useProfile();
  const [openCategory, setOpenCategory] = useState<string>('');

  // Determine which category should be open based on current path
  const getPathCategory = (): string => {
    const path = location.pathname;
    // Find the tool in tools-config and return its category
    const matchedTool = tools.find(t => path.includes(t.path.split('/').pop()!));
    if (matchedTool) return matchedTool.category;
    if (path.includes('/tools/live-sports')) return 'utilities';
    return '';
  };

  // Sync accordion state when path changes (but only if navigating to a tool)
  useEffect(() => {
    const pathCategory = getPathCategory();
    if (pathCategory) {
      setOpenCategory(pathCategory);
    }
  }, [location.pathname]);

  // Close mobile sidebar on navigation
  const prevPathRef = useRef(location.pathname);
  useEffect(() => {
    if (prevPathRef.current !== location.pathname && isMobileOpen && onMobileClose) {
      onMobileClose();
    }
    prevPathRef.current = location.pathname;
  }, [location.pathname]);

  const handleSignOut = async () => {
    try {
      await signOut();
      navigate('/login', { replace: true });
    } catch (error) {
      console.error('Sign out error:', error);
    }
  };

  // Handle accordion change - allow user to control which is open
  const handleAccordionChange = (value: string) => {
    setOpenCategory(value);
  };

  const getInitials = () => {
    if (username) return username.slice(0, 2).toUpperCase();
    if (user?.email) return user.email.slice(0, 2).toUpperCase();
    return 'U';
  };

  const renderToolCategory = (
    value: string,
    label: string,
    Icon: any,
    categoryTools: { label: string; path: string; icon: React.ElementType; isNew?: boolean; isPro?: boolean; badge?: string; badgeColor?: string }[]
  ) => (
    <AccordionItem value={value} className="border-none">
      <AccordionTrigger className={cn(
        "px-3 py-2.5 text-sm font-medium hover:no-underline rounded-xl transition-all duration-300 group",
        "text-muted-foreground hover:text-foreground",
        "hover:bg-gradient-to-r hover:from-primary/10 hover:to-transparent",
        "hover:shadow-[0_0_20px_rgba(59,130,246,0.1)] hover:border-primary/20",
        "data-[state=open]:bg-gradient-to-r data-[state=open]:from-primary/15 data-[state=open]:to-transparent",
        "data-[state=open]:border-l-2 data-[state=open]:border-primary data-[state=open]:text-foreground"
      )}>
        <div className="flex items-center gap-3">
          <div className={cn(
            "p-1.5 rounded-lg transition-all duration-300",
            "group-hover:bg-primary/20 group-hover:shadow-lg group-hover:shadow-primary/20",
            "group-data-[state=open]:bg-primary/20 group-data-[state=open]:shadow-lg group-data-[state=open]:shadow-primary/20"
          )}>
            <Icon className={cn(
              "h-4 w-4 transition-all duration-300",
              "group-hover:text-primary group-hover:scale-110 group-hover:rotate-3",
              "group-data-[state=open]:text-primary group-data-[state=open]:scale-110"
            )} />
          </div>
          <span className="group-hover:translate-x-1 transition-transform duration-300 font-medium">{label}</span>
          <Badge variant="outline" className={cn(
            "ml-auto text-[10px] px-2 py-0.5 h-5 font-semibold transition-all duration-300",
            "opacity-60 group-hover:opacity-100",
            "group-hover:border-primary/50 group-hover:bg-primary/10 group-hover:text-primary",
            "group-data-[state=open]:border-primary/50 group-data-[state=open]:bg-primary/10 group-data-[state=open]:text-primary group-data-[state=open]:opacity-100"
          )}>
            {categoryTools.length}
          </Badge>
        </div>
      </AccordionTrigger>
      <AccordionContent className="pb-0 animate-slide-up">
        <div className="ml-4 mt-1 space-y-1 border-l-2 border-gradient-to-b from-primary/50 to-border/30 pl-3">
          {categoryTools.map((tool, index) => {
            const isActive = location.pathname === tool.path;
            const ToolIcon = tool.icon;

            return (
              <Link
                key={tool.path}
                to={tool.path}
                {...((tool as any).tourId ? { 'data-tour': (tool as any).tourId } : {})}
                style={{ animationDelay: `${index * 0.05}s` }}
                className={cn(
                  "flex items-center gap-2.5 px-3 py-2.5 rounded-xl transition-all duration-300 text-xs group/item",
                  "animate-fade-in opacity-0 [animation-fill-mode:forwards]",
                  isActive
                    ? "bg-gradient-to-r from-primary/25 to-primary/10 text-primary border-l-[3px] border-primary -ml-[15px] pl-[12px] shadow-lg shadow-primary/10"
                    : "text-muted-foreground hover:text-foreground hover:bg-gradient-to-r hover:from-accent/80 hover:to-transparent hover:translate-x-2 hover:shadow-md"
                )}
              >
                <div className={cn(
                  "p-1 rounded-md transition-all duration-300",
                  isActive ? "bg-primary/20" : "group-hover/item:bg-primary/10"
                )}>
                  <ToolIcon className={cn(
                    "h-3.5 w-3.5 transition-all duration-300",
                    isActive ? "text-primary" : "group-hover/item:text-primary group-hover/item:scale-110"
                  )} />
                </div>
                <span className="flex-1 font-medium">{tool.label}</span>
                {(tool as any).isNew && <NewBadge />}
                {(tool as any).badge && (
                  <Badge className={cn("text-[9px] px-1.5 py-0 h-4 border-0 whitespace-nowrap", (tool as any).badgeColor || "bg-primary text-primary-foreground")}>
                    {(tool as any).badge}
                  </Badge>
                )}
                {isActive && (
                  <div className="w-1.5 h-1.5 rounded-full bg-primary animate-pulse shadow-lg shadow-primary" />
                )}
              </Link>
            );
          })}
        </div>
      </AccordionContent>
    </AccordionItem>
  );

  return (
    <aside
      data-tour="sidebar"
      className={cn(
        "fixed left-0 top-0 z-40 h-screen transition-all duration-300 flex flex-col",
        "bg-gradient-to-b from-background via-background to-background/95",
        "border-r border-border/50 backdrop-blur-xl",
        isCollapsed ? "w-16" : "w-64",
        // Mobile styles
        "max-lg:fixed max-lg:left-0 max-lg:top-0 max-lg:z-50",
        isMobileOpen ? "max-lg:translate-x-0" : "max-lg:-translate-x-full"
      )}
    >
      {/* Logo */}
      <div className="flex items-center justify-between p-4 border-b border-border/50">
        <Link
          to="/dashboard"
          className="flex items-center gap-3 group"
        >
          <div className="relative">
            <img
              src={tamLogo}
              alt="TAM Store"
              className={cn(
                "h-10 w-10 rounded-full transition-all duration-300",
                "group-hover:scale-110 group-hover:rotate-3"
              )}
            />
            <div className="absolute inset-0 rounded-full bg-primary/20 blur-xl opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
          </div>
          {!isCollapsed && (
            <span className="text-lg font-bold gradient-text transition-all duration-300 group-hover:tracking-wide">
              TAM Store
            </span>
          )}
        </Link>
        {/* Close button on mobile */}
        {isMobileOpen && onMobileClose && (
          <Button variant="ghost" size="icon" onClick={onMobileClose} className="lg:hidden">
            <ChevronLeft className="h-5 w-5" />
          </Button>
        )}
      </div>

      {/* Navigation */}
      <ScrollArea className="flex-1 py-3">
        <nav className="px-3 space-y-1">
          {/* Main Nav */}
          {mainNavItems.map((item) => {
            const isActive = location.pathname === item.path;
            const isLocked = item.requiresPremium && !isPremium;

            return (
              <Link
                key={item.path}
                to={isLocked ? '#' : item.path}
                {...((item as any).tourId ? { 'data-tour': (item as any).tourId } : {})}
                className={cn(
                  "flex items-center gap-3 px-3 py-3 rounded-xl transition-all duration-300 group relative overflow-hidden",
                  isActive
                    ? "bg-gradient-to-r from-primary/25 via-primary/15 to-transparent text-primary shadow-lg shadow-primary/10"
                    : "text-muted-foreground hover:text-foreground hover:bg-gradient-to-r hover:from-accent/80 hover:to-transparent hover:shadow-md hover:translate-x-1",
                  isLocked && "opacity-50 cursor-not-allowed hover:translate-x-0"
                )}
                onClick={(e) => isLocked && e.preventDefault()}
              >
                {/* Animated background glow on hover */}
                <div className={cn(
                  "absolute inset-0 opacity-0 transition-opacity duration-500",
                  "bg-gradient-to-r from-primary/10 via-primary/5 to-transparent",
                  "group-hover:opacity-100"
                )} />

                {/* Active indicator - animated bar */}
                {isActive && (
                  <div className="absolute left-0 top-1/2 -translate-y-1/2 w-1 h-8 bg-gradient-to-b from-primary via-primary to-primary/50 rounded-r-full shadow-[0_0_15px_rgba(59,130,246,0.6)] animate-pulse" />
                )}

                {/* Icon with background */}
                <div className={cn(
                  "relative p-2 rounded-lg transition-all duration-300 z-10",
                  isActive
                    ? "bg-primary/20 shadow-lg shadow-primary/20"
                    : "group-hover:bg-primary/10 group-hover:shadow-md"
                )}>
                  <item.icon className={cn(
                    "h-5 w-5 flex-shrink-0 transition-all duration-300",
                    isActive && "text-primary",
                    !isActive && "group-hover:scale-110 group-hover:text-primary group-hover:rotate-3"
                  )} />
                </div>

                {!isCollapsed && (
                  <>
                    <span className={cn(
                      "flex-1 text-sm font-semibold z-10 transition-all duration-300",
                      "group-hover:translate-x-0.5"
                    )}>{item.label}</span>
                    {(item as any).badge && (
                      <Badge className={cn(
                        "text-[10px] px-2 py-0.5 border-0 shadow-sm font-bold",
                        (item as any).badgeColor || "bg-primary text-primary-foreground"
                      )}>
                        {(item as any).badge}
                      </Badge>
                    )}
                    {(item as any).isPro && (
                      <Badge className="bg-gradient-to-r from-yellow-500 to-amber-500 text-white text-[10px] px-2 py-0.5 border-0 shadow-lg shadow-yellow-500/30">
                        PRO
                      </Badge>
                    )}
                    {isLocked && <Lock className="h-4 w-4" />}
                    {isActive && (
                      <div className="w-2 h-2 rounded-full bg-primary animate-ping" />
                    )}
                  </>
                )}
              </Link>
            );
          })}

          {/* Separator */}
          {!isCollapsed && (
            <div className="my-4 mx-3 h-px bg-gradient-to-r from-transparent via-border to-transparent" />
          )}

          {/* Categories Accordion - AI Tools FIRST */}
          {!isCollapsed && (
            <Accordion
              type="single"
              collapsible
              value={openCategory}
              onValueChange={handleAccordionChange}
              className="space-y-1"
            >
              {sidebarCategories.map(cat => {
                const catTools = cat === 'utilities' && isPremium
                  ? [...getToolsForCategory(cat), liveSportsTool]
                  : getToolsForCategory(cat);
                return renderToolCategory(cat, categoryLabels[cat], categoryIcons[cat], catTools);
              })}
            </Accordion>
          )}

          {/* Admin Link */}
          {!isCollapsed && isAdmin && (
            <>
              <div className="my-4 mx-3 h-px bg-gradient-to-r from-transparent via-border to-transparent" />
              <Link
                to="/dashboard/admin"
                className={cn(
                  "flex items-center gap-3 px-3 py-2.5 rounded-lg transition-all duration-200 group",
                  location.pathname.startsWith('/dashboard/admin')
                    ? "bg-primary/20 text-primary"
                    : "text-muted-foreground hover:text-foreground hover:bg-accent/50"
                )}
              >
                <Shield className="h-5 w-5" />
                <span className="text-sm font-medium">Admin Panel</span>
                <Badge variant="outline" className="ml-auto text-[10px] px-1.5 py-0 h-4 border-primary/50 text-primary">
                  Admin
                </Badge>
              </Link>
            </>
          )}
        </nav>
      </ScrollArea>

      {/* Bottom Section - User Info & Actions */}
      <div className="p-3 border-t border-border/50 space-y-2">
        {/* User Info (when expanded and logged in) */}
        {!isCollapsed && user && (
          <div data-tour="profile" className="flex items-center gap-3 px-3 py-2 rounded-lg bg-accent/30">
            <Avatar className="h-8 w-8">
              <AvatarImage src={profile?.avatar_url || undefined} />
              <AvatarFallback className="bg-primary/20 text-primary text-xs">
                {getInitials()}
              </AvatarFallback>
            </Avatar>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium truncate flex items-center gap-1">
                {username || 'User'}
                {isPremium && premiumTier && (
                  <PremiumBadge tier={premiumTier} size="sm" />
                )}
              </p>
              <p className="text-xs text-muted-foreground truncate">{user?.email}</p>
            </div>
          </div>
        )}

        {/* Collapse Button */}
        <Button
          variant="ghost"
          size={isCollapsed ? "icon" : "default"}
          onClick={onToggle}
          className={cn(
            "w-full transition-all duration-200 hover:bg-accent/50",
            !isCollapsed && "justify-start"
          )}
        >
          {isCollapsed ? (
            <ChevronRight className="h-5 w-5" />
          ) : (
            <>
              <ChevronLeft className="h-5 w-5 mr-2" />
              <span>Collapse</span>
            </>
          )}
        </Button>

        {/* Logout Button (only when logged in) */}
        {user && (
          <Button
            variant="ghost"
            size={isCollapsed ? "icon" : "default"}
            onClick={handleSignOut}
            className={cn(
              "w-full text-muted-foreground hover:text-destructive hover:bg-destructive/10 transition-all duration-200",
              !isCollapsed && "justify-start"
            )}
          >
            <LogOut className={cn("h-5 w-5", !isCollapsed && "mr-2")} />
            {!isCollapsed && <span>Logout</span>}
          </Button>
        )}
      </div>

      {/* Custom scrollbar styles */}
      <style>{`
        .scrollbar-thin::-webkit-scrollbar {
          width: 4px;
        }
        .scrollbar-thin::-webkit-scrollbar-track {
          background: transparent;
        }
        .scrollbar-thin::-webkit-scrollbar-thumb {
          background: hsl(var(--border));
          border-radius: 2px;
        }
        .scrollbar-thin::-webkit-scrollbar-thumb:hover {
          background: hsl(var(--primary) / 0.5);
        }
      `}</style>
    </aside>
  );
};

export default Sidebar;
